/**
 * Client-side 1080×1080 social collage using Canvas 2D (object-fit: cover per cell).
 * Layouts match docs/project-management/2-in-progress/admin-bundle-social-post/phased-implementation.md §4.3.
 */

export const DEFAULT_SOCIAL_GRID_SIZE = 1080;

const PLACEHOLDER_FILL = "#e5e7eb";

/** Per-cell text drawn on a bottom bar (title + price | location). */
export type SocialGridCellLabel = {
  title: string;
  /** Pre-formatted second line, e.g. `₦10,000 | Lagos`. */
  priceLocationLine: string;
};

export type ComposeSocialGridOptions = {
  /** Canvas width/height in pixels (default 1080). */
  size?: number;
  mimeType?: "image/jpeg" | "image/png";
  /** JPEG quality 0–1 (default 0.92). Ignored for PNG. */
  quality?: number;
  /**
   * When provided (same length as `imageUrls`), each tile gets a semi-transparent
   * bottom overlay with title and price/location for sharing without the caption.
   */
  cellLabels?: SocialGridCellLabel[];
  /**
   * For 2-image collages, choose split direction:
   * - "horizontal": left and right tiles (default)
   * - "vertical": top and bottom tiles
   */
  twoImageOrientation?: "horizontal" | "vertical";
  /**
   * Optional branding controls for exported images.
   */
  branding?: {
    /** Subtle center watermark logo across each tile. */
    showWatermark?: boolean;
    /** Logo badge rendered inside the bottom overlay bar. */
    showOverlayLogo?: boolean;
    /** Logo asset URL (same-origin/static recommended). */
    logoUrl?: string;
  };
};

export type CellRect = { x: number; y: number; w: number; h: number };

/**
 * Layout rectangles for n items on an S×S canvas (row-major where applicable).
 */
export function getSocialGridCellRects(
  count: number,
  size: number = DEFAULT_SOCIAL_GRID_SIZE,
  options?: { twoImageOrientation?: "horizontal" | "vertical" },
): CellRect[] {
  const S = size;
  switch (count) {
    case 1:
      return [{ x: 0, y: 0, w: S, h: S }];
    case 2:
      if (options?.twoImageOrientation === "vertical") {
        return [
          { x: 0, y: 0, w: S, h: S / 2 },
          { x: 0, y: S / 2, w: S, h: S / 2 },
        ];
      }
      return [
        { x: 0, y: 0, w: S / 2, h: S },
        { x: S / 2, y: 0, w: S / 2, h: S },
      ];
    case 3:
      return [
        { x: 0, y: 0, w: S, h: S / 2 },
        { x: 0, y: S / 2, w: S / 2, h: S / 2 },
        { x: S / 2, y: S / 2, w: S / 2, h: S / 2 },
      ];
    case 4:
      return [
        { x: 0, y: 0, w: S / 2, h: S / 2 },
        { x: S / 2, y: 0, w: S / 2, h: S / 2 },
        { x: 0, y: S / 2, w: S / 2, h: S / 2 },
        { x: S / 2, y: S / 2, w: S / 2, h: S / 2 },
      ];
    case 5:
      return [
        { x: 0, y: 0, w: S / 3, h: S / 2 },
        { x: S / 3, y: 0, w: S / 3, h: S / 2 },
        { x: (2 * S) / 3, y: 0, w: S / 3, h: S / 2 },
        { x: 0, y: S / 2, w: S / 2, h: S / 2 },
        { x: S / 2, y: S / 2, w: S / 2, h: S / 2 },
      ];
    case 6: {
      const cw = S / 2;
      const ch = S / 3;
      return [
        { x: 0, y: 0, w: cw, h: ch },
        { x: cw, y: 0, w: cw, h: ch },
        { x: 0, y: ch, w: cw, h: ch },
        { x: cw, y: ch, w: cw, h: ch },
        { x: 0, y: 2 * ch, w: cw, h: ch },
        { x: cw, y: 2 * ch, w: cw, h: ch },
      ];
    }
    default:
      throw new Error(
        `getSocialGridCellRects: count must be 1–6, got ${count}`,
      );
  }
}

async function fetchImageBitmap(url: string): Promise<ImageBitmap | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await createImageBitmap(blob);
  } catch {
    return null;
  }
}

async function fetchLogoBitmap(url: string): Promise<ImageBitmap | null> {
  const viaFetch = await fetchImageBitmap(url);
  if (viaFetch) return viaFetch;

  try {
    const img = new Image();
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("logo image load failed"));
      img.src = url;
    });
    return await createImageBitmap(img);
  } catch {
    return null;
  }
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  bitmap: ImageBitmap,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const sw = bitmap.width;
  const sh = bitmap.height;
  if (sw <= 0 || sh <= 0) return;

  const destAspect = dw / dh;
  const srcAspect = sw / sh;
  let sx: number;
  let sy: number;
  let sWidth: number;
  let sHeight: number;

  if (srcAspect > destAspect) {
    sHeight = sh;
    sWidth = sh * destAspect;
    sx = (sw - sWidth) / 2;
    sy = 0;
  } else {
    sWidth = sw;
    sHeight = sw / destAspect;
    sx = 0;
    sy = (sh - sHeight) / 2;
  }

  ctx.drawImage(bitmap, sx, sy, sWidth, sHeight, dx, dy, dw, dh);
}

function truncateToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return "";
  }
  if (ctx.measureText(trimmed).width <= maxWidth) {
    return trimmed;
  }
  const ell = "…";
  let low = 0;
  let high = trimmed.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    const candidate = trimmed.slice(0, mid) + ell;
    if (ctx.measureText(candidate).width <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return trimmed.slice(0, low) + ell;
}

function drawCellBottomOverlay(
  ctx: CanvasRenderingContext2D,
  cell: CellRect,
  label: SocialGridCellLabel,
  logoBitmap: ImageBitmap | null,
  showOverlayLogo: boolean,
): void {
  const { x, y, w, h } = cell;
  const pad = Math.max(5, Math.floor(Math.min(w, h) * 0.028));
  const maxBarH = Math.floor(h * 0.42);
  let titleSize = Math.round(
    Math.max(11, Math.min(26, Math.min(w, h) * 0.056)),
  );
  let subSize = Math.max(9, Math.round(titleSize * 0.82));
  const lineGap = Math.max(2, Math.round(titleSize * 0.12));

  const titleText = label.title.trim() || "—";
  const subText = label.priceLocationLine.trim() || "—";

  const measureOverlayH = (ts: number, ss: number) =>
    pad * 2 + ts * 1.2 + lineGap + ss * 1.15;

  let overlayH = measureOverlayH(titleSize, subSize);
  while (overlayH > maxBarH && titleSize > 9) {
    titleSize -= 1;
    subSize = Math.max(8, Math.round(titleSize * 0.82));
    overlayH = measureOverlayH(titleSize, subSize);
  }

  const barY = y + h - overlayH;
  ctx.fillStyle = "rgba(0,0,0,0.72)";
  ctx.fillRect(x, barY, w, overlayH);

  const logoSize = Math.max(0, Math.round(overlayH - pad * 2));
  const canDrawLogo = Boolean(showOverlayLogo && logoBitmap && logoSize > 0);
  const logoReserve = canDrawLogo ? logoSize + pad : 0;
  const textMaxW = Math.max(0, w - pad * 2 - logoReserve);
  let textY = barY + pad;

  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "top";
  ctx.font = `600 ${titleSize}px system-ui, -apple-system, sans-serif`;
  const line1 = truncateToWidth(ctx, titleText, textMaxW);
  ctx.fillText(line1, x + pad, textY);
  textY += titleSize * 1.2 + lineGap;

  ctx.font = `400 ${subSize}px system-ui, -apple-system, sans-serif`;
  const line2 = truncateToWidth(ctx, subText, textMaxW);
  ctx.fillText(line2, x + pad, textY);

  if (canDrawLogo && logoBitmap) {
    const rawW = logoBitmap.width || 1;
    const rawH = logoBitmap.height || 1;
    const aspect = rawW / rawH;
    let dw = logoSize;
    let dh = logoSize;
    if (aspect >= 1) {
      dh = Math.max(1, Math.round(dw / aspect));
    } else {
      dw = Math.max(1, Math.round(dh * aspect));
    }
    const dx = x + w - pad - dw;
    const dy = barY + Math.round((overlayH - dh) / 2);
    ctx.save();
    // Ensure logo remains visible on dark overlay regardless of logo colors.
    const badgePad = Math.max(2, Math.round(pad * 0.5));
    const bx = dx - badgePad;
    const by = dy - badgePad;
    const bw = dw + badgePad * 2;
    const bh = dh + badgePad * 2;
    const r = Math.max(4, Math.round(Math.min(bw, bh) * 0.2));
    ctx.fillStyle = "rgba(255,255,255,0.88)";
    ctx.beginPath();
    ctx.moveTo(bx + r, by);
    ctx.lineTo(bx + bw - r, by);
    ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
    ctx.lineTo(bx + bw, by + bh - r);
    ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
    ctx.lineTo(bx + r, by + bh);
    ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
    ctx.lineTo(bx, by + r);
    ctx.quadraticCurveTo(bx, by, bx + r, by);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 0.98;
    ctx.drawImage(logoBitmap, dx, dy, dw, dh);
    ctx.restore();
  }
}

function drawTileWatermark(
  ctx: CanvasRenderingContext2D,
  cell: CellRect,
  logoBitmap: ImageBitmap | null,
): void {
  if (!logoBitmap) return;
  const { x, y, w, h } = cell;
  const target = Math.max(26, Math.round(Math.min(w, h) * 0.28));
  const rawW = logoBitmap.width || 1;
  const rawH = logoBitmap.height || 1;
  const aspect = rawW / rawH;
  let dw = target;
  let dh = target;
  if (aspect >= 1) {
    dh = Math.max(1, Math.round(dw / aspect));
  } else {
    dw = Math.max(1, Math.round(dh * aspect));
  }
  const dx = x + Math.round((w - dw) / 2);
  const dy = y + Math.round((h - dh) / 2);
  ctx.save();
  ctx.globalAlpha = 0.24;
  ctx.drawImage(logoBitmap, dx, dy, dw, dh);
  ctx.restore();
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("canvas.toBlob returned null"));
        }
      },
      mimeType,
      quality,
    );
  });
}

/**
 * Builds a single square collage image from 1–6 image URLs (same order as items).
 * Missing or failed URLs render as a neutral placeholder tile.
 */
export async function composeSocialGridImage(
  imageUrls: (string | null | undefined)[],
  options: ComposeSocialGridOptions = {},
): Promise<Blob> {
  const n = imageUrls.length;
  if (n < 1 || n > 6) {
    throw new Error(
      `composeSocialGridImage: expected 1–6 image slots, got ${n}`,
    );
  }

  const { cellLabels, twoImageOrientation } = options;
  if (cellLabels !== undefined && cellLabels.length !== n) {
    throw new Error(
      `composeSocialGridImage: cellLabels length (${cellLabels.length}) must match imageUrls (${n})`,
    );
  }

  const size = options.size ?? DEFAULT_SOCIAL_GRID_SIZE;
  const mimeType = options.mimeType ?? "image/jpeg";
  const quality =
    mimeType === "image/jpeg" ? (options.quality ?? 0.92) : undefined;

  const rects = getSocialGridCellRects(n, size, { twoImageOrientation });
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2D canvas context");
  }

  const bitmaps = await Promise.all(
    imageUrls.map((url) =>
      url && url.trim().length > 0 ? fetchImageBitmap(url.trim()) : null,
    ),
  );

  const branding = options.branding;
  const showWatermark = Boolean(branding?.showWatermark);
  const showOverlayLogo = Boolean(branding?.showOverlayLogo);
  const logoUrl = branding?.logoUrl?.trim();
  let logoBitmap: ImageBitmap | null = null;
  if ((showWatermark || showOverlayLogo) && logoUrl) {
    logoBitmap = await fetchLogoBitmap(logoUrl);
  }

  for (let i = 0; i < n; i++) {
    const { x, y, w, h } = rects[i];
    const bitmap = bitmaps[i];
    if (bitmap) {
      try {
        drawImageCover(ctx, bitmap, x, y, w, h);
      } finally {
        bitmap.close();
      }
    } else {
      ctx.fillStyle = PLACEHOLDER_FILL;
      ctx.fillRect(x, y, w, h);
    }

    if (showWatermark) {
      drawTileWatermark(ctx, rects[i], logoBitmap);
    }

    if (cellLabels?.[i]) {
      drawCellBottomOverlay(
        ctx,
        rects[i],
        cellLabels[i],
        logoBitmap,
        showOverlayLogo,
      );
    }
  }

  if (logoBitmap) {
    logoBitmap.close();
  }

  return canvasToBlob(canvas, mimeType, quality);
}
